import axios from "../axiosApi";
import config from "../../config";

export interface ChamadaPdfFrequencyCell {
    status: number | string;
    dt: string | null;
    dofCid: number | null;
    id: number;
}

export interface ChamadaPdfStudent {
    PaymentCard?: number | string;
    FrequenciaPercentual?: number | string;
    Nota?: number | string;
    name?: string;
    Name?: string;
    frequencia: ChamadaPdfFrequencyCell[];
    [key: string]: any;
}

export interface ChamadaPdfData {
    datas: (string | null)[];
    alunosdados: ChamadaPdfStudent[];
    conteudo: any[];
    header: any;
    disciplina: string;
    etapa: string;
    nivel: string;
    turma: string;
    sala: string;
    turno: string;
    periodo: string;
    cH: number | string;
    schoolAuthorizationOfficialNumber: string;
}

const getRequestErrorMessage = (label: string, error: any): string => {
    const status = error?.response?.status;
    const responseMessage = error?.response?.data?.error?.message || error?.response?.data?.message;

    if (status) {
        return `${label} retornou HTTP ${status}${responseMessage ? `: ${responseMessage}` : ""}`;
    }

    if (error?.code === "ECONNABORTED") {
        return `${label} demorou demais para responder`;
    }

    return `${label} falhou${error?.message ? `: ${error.message}` : ""}`;
};

const getWithRetry = async (url: string, label: string, retries = 1) => {
    let lastError: any = null;

    for (let attempt = 0; attempt <= retries; attempt += 1) {
        try {
            return await axios.get(url);
        } catch (error: any) {
            lastError = error;
            if (attempt < retries) {
                await new Promise((resolve) => setTimeout(resolve, 500));
            }
        }
    }

    throw new Error(getRequestErrorMessage(label, lastError));
};

const enrichHeaderDisciplineLevel = async (header: any) => {
    const disciplineId = header?.disciplineModule?.discipline?.id;
    if (!disciplineId) return header;

    const disciplineFilter = JSON.stringify({
        where: { id: disciplineId },
        include: [
            {
                relation: "level",
                scope: {
                    include: ["schoolAuthorization"],
                },
            },
        ],
    });
    const response = await axios.get(`/Disciplines?filter=${encodeURIComponent(disciplineFilter)}`);
    const disciplineWithLevel = response.data?.[0];
    if (disciplineWithLevel) {
        header.disciplineModule.discipline = {
            ...header.disciplineModule.discipline,
            ...disciplineWithLevel,
        };
    }
    return header;
};

/**
 * Fetches all frequency data for a given DisciplineOffer.
 * Calls 4 endpoints and combines the results:
 *   1. Students list (getStudentsListForFrequencyCallPrint)
 *   2. Frequency records (getFrequencyCallForPrint)
 *   3. DOF contents / class sessions (DisciplineOfferContents)
 *   4. DOF header info (DisciplineOffers with includes)
 *
 * Returns the assembled data object directly (no Redux dispatch -- local state).
 */
export const fetchFrequenciaData = (dofId: number) => {
    return async (dispatch: any) => {
        try {
            // Run all 4 requests in parallel
            const [alunosRes, frequenciaRes, contentsRes, headerRes] = await Promise.all([
                axios.get(
                    config.BASE_URL +
                    `/DisciplineOffers/getStudentsListForFrequencyCallPrint?params={"dofId":${dofId}}`
                ),
                axios.get(
                    config.BASE_URL +
                    `/DisciplineOffers/getFrequencyCallForPrint?params={"dofId":${dofId}}`
                ),
                axios.get(
                    `/DisciplineOfferContents?filter=${encodeURIComponent(
                        JSON.stringify({
                            where: { DisciplineOffer_id: dofId },
                            order: "Date ASC",
                        })
                    )}`
                ),
                axios.get(
                    `/DisciplineOffers?filter=${encodeURIComponent(
                        JSON.stringify({
                            where: { id: dofId },
                            include: [
                                { classOffer: "level" },
                                {
                                    relation: "disciplineModule",
                                    scope: {
                                        include: ["discipline"],
                                    },
                                },
                                "teacher",
                                "period",
                            ],
                        })
                    )}`
                ),
            ]);

            const alunos = alunosRes.data || [];
            const frequencia = frequenciaRes.data || [];
            const contents = contentsRes.data || [];
            const header = headerRes.data?.[0] || {};
            await enrichHeaderDisciplineLevel(header);

            // Build date columns: expand each content by QtyHours
            const dateColumns: { contentId: number; date: string; hourIndex: number }[] = [];
            contents.forEach((c: any) => {
                const qtyHours = c.QtyHours || 1;
                for (let h = 0; h < qtyHours; h++) {
                    dateColumns.push({
                        contentId: c.id,
                        date: c.Date,
                        hourIndex: h,
                    });
                }
            });

            // For each student, build frequency status per date column
            const studentsWithFrequency = alunos.map((aluno: any) => {
                // Get all regular-class frequency records for this student
                const studentFreq = frequencia.filter(
                    (f: any) => f.DisciplineEntree_id === aluno.deeId && f.Type === 1
                );

                // Build status array matching dateColumns order
                const statuses: (number | null)[] = [];
                contents.forEach((c: any) => {
                    const contentFreq = studentFreq.filter(
                        (f: any) => f.DisciplineOfferContent_id === c.id
                    );
                    const qtyHours = c.QtyHours || 1;
                    for (let h = 0; h < qtyHours; h++) {
                        statuses.push(
                            contentFreq[h] != null ? contentFreq[h].Status : null
                        );
                    }
                });

                // Compute summary
                let totalP = 0;
                let totalF = 0;
                let totalJ = 0;
                statuses.forEach((s) => {
                    if (s === 1) totalP++;
                    else if (s === 0) totalF++;
                    else if (s === 2) totalJ++;
                });
                const totalClasses = totalP + totalF + totalJ;
                const freqPercent = totalClasses > 0
                    ? Math.round((totalP / totalClasses) * 10000) / 100
                    : 0;

                return {
                    name: aluno.Name || aluno.name || '',
                    deeId: aluno.deeId,
                    enrollmentId: aluno.enrollmentId,
                    statuses,
                    totalP,
                    totalF,
                    totalJ,
                    freqPercent,
                };
            });

            // Sort students alphabetically
            studentsWithFrequency.sort((a: any, b: any) =>
                (a.name || '').localeCompare(b.name || '')
            );

            return {
                header,
                contents,
                dateColumns,
                students: studentsWithFrequency,
            };
        } catch (e) {
            console.error("fetchFrequenciaData ERROR", e);
            throw e;
        }
    };
};

/**
 * Fetches and assembles the legacy "Chamadas PDF" shape.
 * The printable page keeps the old frequency-call layout, while generation uses
 * the project's current react-to-print pipeline instead of @react-pdf/renderer.
 */
export const fetchChamadaPdfData = (dofId: number) => {
    return async (_dispatch: any): Promise<ChamadaPdfData> => {
        try {
            const params = encodeURIComponent(JSON.stringify({ dofId }));
            const headerFilter = encodeURIComponent(JSON.stringify({
                where: { id: dofId },
                include: [
                    {
                        relation: "classOffer",
                        scope: {
                            include: [
                                {
                                    relation: "level",
                                    scope: {
                                        include: ["schoolAuthorization"],
                                    },
                                },
                            ],
                        },
                    },
                    {
                        relation: "discipline",
                        scope: {
                            include: [
                                {
                                    relation: "level",
                                    scope: {
                                        include: ["schoolAuthorization"],
                                    },
                                },
                            ],
                        },
                    },
                    "disciplineModule",
                    "teacher",
                    "period",
                ],
            }));
            const contentsFilter = encodeURIComponent(JSON.stringify({
                where: { DisciplineOffer_id: dofId },
                order: "Date ASC",
            }));

            const [alunosRes, frequenciaRes, headerRes, contentsRes] = await Promise.all([
                getWithRetry(
                    `/DisciplineOffers/getStudentsListForFrequencyCallPrint?params=${params}`,
                    "Lista de alunos da chamada"
                ),
                getWithRetry(
                    `/DisciplineOffers/getFrequencyCallForPrint?params=${params}`,
                    "Lancamentos de frequencia"
                ),
                getWithRetry(
                    `/DisciplineOffers?filter=${headerFilter}`,
                    "Cabecalho da disciplina ofertada"
                ),
                getWithRetry(
                    `/DisciplineOfferContents?filter=${contentsFilter}`,
                    "Conteudos/aulas da disciplina ofertada"
                ),
            ]);

            const alunos = alunosRes.data || [];
            const frequencia = frequenciaRes.data || [];
            const header = headerRes.data?.[0] || {};
            const conteudo = contentsRes.data || [];
            const workload = Number(header?.disciplineModule?.WorkloadPresential) || 0;
            await enrichHeaderDisciplineLevel(header);

            if (!header?.id) {
                throw new Error(`Disciplina Ofertada ${dofId} nao encontrada.`);
            }

            const schoolAuthorizationOfficialNumber =
                header?.discipline?.level?.schoolAuthorization?.OfficialNumber ||
                header?.classOffer?.level?.schoolAuthorization?.OfficialNumber ||
                "";

            const datas: (string | null)[] = [];
            conteudo.forEach((content: any) => {
                const qtyHours = Number(content?.QtyHours) || 1;
                for (let hour = 0; hour < qtyHours; hour += 1) {
                    datas.push(content?.Date || null);
                }
            });
            while (datas.length < workload) {
                datas.push(null);
            }

            const alunosdados = alunos.map((aluno: any) => {
                const alunoFrequencia = frequencia.filter(
                    (freq: any) => freq.DisciplineEntree_id === aluno.deeId && freq.Type === 1
                );
                const frequenciaLinha: ChamadaPdfFrequencyCell[] = [];

                conteudo.forEach((content: any) => {
                    const contentFrequencies = alunoFrequencia.filter(
                        (freq: any) => freq.DisciplineOfferContent_id === content.id
                    );
                    const qtyHours = Number(content?.QtyHours) || 1;

                    for (let hour = 0; hour < qtyHours; hour += 1) {
                        const freq = contentFrequencies[hour];
                        frequenciaLinha.push(freq ? {
                            status: freq.Status,
                            dt: freq.Date || content.Date || null,
                            dofCid: freq.DisciplineOfferContent_id || content.id,
                            id: freq.id || 0,
                        } : {
                            status: "-",
                            dt: content.Date || null,
                            dofCid: content.id || null,
                            id: 0,
                        });
                    }
                });

                while (frequenciaLinha.length < workload) {
                    frequenciaLinha.push({
                        status: " ",
                        dt: null,
                        dofCid: null,
                        id: 0,
                    });
                }

                return {
                    ...aluno,
                    name: aluno.name || aluno.Name || "",
                    frequencia: frequenciaLinha,
                };
            });

            return {
                datas,
                alunosdados,
                conteudo,
                header,
                disciplina: header?.discipline?.Description || "",
                etapa: header?.disciplineModule?.Description || "",
                nivel: header?.discipline?.level?.Description || header?.classOffer?.level?.Description || "",
                turma: header?.classOffer?.Description || "",
                sala: header?.classOffer?.Room || "",
                turno: header?.classOffer?.Shift || "",
                periodo: header?.period?.Description || "",
                cH: header?.disciplineModule?.WorkloadPresential || "",
                schoolAuthorizationOfficialNumber,
            };
        } catch (e) {
            console.error("fetchChamadaPdfData ERROR", e);
            throw e;
        }
    };
};
